import { t } from "@lingui/macro";
import {
  type GetRequestsForInvoicingQuery,
  RequestForInvoicingOrderByColumnEnum,
  RequestForInvoicingTypeEnum,
  RequestsForInvoicingWhereColumn,
  SortOrder,
  SqlOperator,
} from "@src/__generated__/graphql";
import type {
  GetRequestsForInvoicingSummaryQuery,
  RequestsForInvoicingFiltersQuery,
} from "@src/__generated__/urql-graphql";
import { appStore } from "@src/stores/AppStore";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { camelCase } from "lodash";
import { computed, makeObservable, observable } from "mobx";
import mapToOptions from "../../../../../utils/map-to-options";
import { RequestForInvoicingModel } from "./models";

export class StoreRequestsForInvoicing {
  refetch?: () => void;
  pagination = new PaginationState("requestsForInvoicing");

  orderBy = new OrderBy<RequestForInvoicingOrderByColumnEnum>([
    { column: RequestForInvoicingOrderByColumnEnum.Id, order: SortOrder.Desc },
  ]);

  where = new Filters<RequestsForInvoicingWhereColumn>(
    [
      new Filter({
        column: RequestsForInvoicingWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        column: RequestsForInvoicingWhereColumn.OwnerId,
        operator: SqlOperator.In,
        title: t`Project Manager`,
        options: [],
        hasSelectAllOption: true,
      }),
      new Filter({
        column: RequestsForInvoicingWhereColumn.Status,
        operator: SqlOperator.Eq,
        title: t`Status`,
        hidden: true,
        options: [],
      }),
      new Filter({
        column: RequestsForInvoicingWhereColumn.WorkspaceCompanyId,
        operator: SqlOperator.In,
        title: t`Legal entity`,
        options: [],
      }),
      new Filter({
        column: RequestsForInvoicingWhereColumn.WorkspaceBankAccountId,
        operator: SqlOperator.In,
        title: t`Agency bank account`,
        options: [],
      }),
      new Filter({
        column: RequestsForInvoicingWhereColumn.Type,
        operator: SqlOperator.Eq,
        title: t`Type`,
        options: [
          {
            label: t`Invoice`,
            value: camelCase(RequestForInvoicingTypeEnum.Invoice),
          },
          {
            label: t`Proforma`,
            value: camelCase(RequestForInvoicingTypeEnum.Proforma),
          },
          {
            label: t`Credit note`,
            value: camelCase(RequestForInvoicingTypeEnum.CreditNote),
          },
        ],
      }),
      new Filter({
        column: RequestsForInvoicingWhereColumn.CreatedAt,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Created date`,
        options: [],
      }),
    ],
    {
      onChange: this.pagination.resetPage,
      tableKey: "requests-for-invoicing",
      orderBy: this.orderBy,
    },
  );

  @observable searchTerm = "";

  @observable.ref requestsForInvoicing: RequestForInvoicingModel[] = [];

  @observable.ref allRequestsForInvoicingIds: RequestForInvoicingModel["id"][] =
    [];

  /**
   * used in bulk actions
   */
  @observable.ref
  selectedRequestsForInvoicingIds: RequestForInvoicingModel["id"][] = [];

  constructor() {
    makeObservable(this);
  }

  fromGetRequestsForInvoicingQuery(data: GetRequestsForInvoicingQuery) {
    this.requestsForInvoicing = (data.requestsForInvoicing?.data || []).map(
      (src) => new RequestForInvoicingModel(src),
    );
    if (data.requestsForInvoicing) {
      this.pagination.setFromPaginatorInfo(
        data.requestsForInvoicing.paginatorInfo,
      );
    }
  }

  setFilterOptions(data: RequestsForInvoicingFiltersQuery) {
    this.where.filtersByColumn
      .get(RequestsForInvoicingWhereColumn.OwnerId)!
      .setOptions(
        mapToOptions.projectManagers.toOptions(data.projectManagerSimpleMap),
      );

    this.where.filtersByColumn
      .get(RequestsForInvoicingWhereColumn.BrandId)
      ?.setOptions(
        data.brandsSimpleMap.map((i) => ({
          value: i.id,
          label: i.name,
          options: i.brands.map((brand) => {
            return {
              label: brand.name,
              value: brand.id,
            };
          }),
        })),
      );

    this.where.filtersByColumn
      .get(RequestsForInvoicingWhereColumn.WorkspaceCompanyId)
      ?.setOptions(
        mapToOptions.workspaceCompanies(appStore.workspaceStore.companies),
      );

    this.where.filtersByColumn
      .get(RequestsForInvoicingWhereColumn.WorkspaceBankAccountId)
      ?.setOptions(
        appStore.workspaceStore.companies
          .flatMap(({ bankAccounts }) => bankAccounts)
          .filter(({ deleted }) => !deleted)
          .map(({ id, iban }) => ({ value: id, label: iban })),
      );
  }

  setAllInvoicingIds(data: GetRequestsForInvoicingSummaryQuery) {
    this.allRequestsForInvoicingIds =
      data.requestsForInvoicingSummary.requestForInvoicingIds;
  }

  static create() {
    return new StoreRequestsForInvoicing();
  }

  get statusFilter() {
    return this.where.filtersByColumn.get(
      RequestsForInvoicingWhereColumn.Status,
    )!;
  }

  @computed get searchParams() {
    return commonSearchParams(this);
  }

  @computed get queryParams() {
    return commonQueryVariables(this);
  }
}
